import type { ReactNode } from 'react';
import { EllipsisVertical } from 'lucide-react';
import { IconButton } from '../icon-button/icon-button.tsx';
import { Menu, MenuContent, MenuItem, MenuTrigger } from '../menu/menu.tsx';

export interface TableActionItem {
  label: string;
  /** A lucide icon element — `<Trash2 />`. The menu sizes and colours it. */
  icon?: ReactNode;
  onSelect: () => void;
  tone?: 'danger';
  disabled?: boolean;
}

export interface TableActionsMenuProps {
  items: TableActionItem[];
  /** The trigger's accessible name. Say what row it belongs to where you can. */
  label?: string;
}

/**
 * The kebab at the end of a row.
 *
 * Nothing is rendered for an empty list: a menu that opens on nothing is worse
 * than a column that is not there. Which actions a row gets — and whether a
 * disabled one is shown at all — is decided by the caller, per row.
 */
export function TableActionsMenu({
  items,
  label = 'Row actions',
}: TableActionsMenuProps) {
  if (items.length === 0) {
    return null;
  }

  return (
    <Menu>
      <MenuTrigger
        render={<IconButton aria-label={label} size='sm' variant='quiet' touch />}
      >
        <EllipsisVertical className='size-4' aria-hidden='true' />
      </MenuTrigger>
      <MenuContent align='end'>
        {items.map((item) => (
          <MenuItem
            key={item.label}
            tone={item.tone === 'danger' ? 'danger' : 'default'}
            disabled={item.disabled}
            onClick={item.onSelect}
          >
            {item.icon}
            {item.label}
          </MenuItem>
        ))}
      </MenuContent>
    </Menu>
  );
}
